import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import HubertLabel from 'components/HubertUI/HubertLabel';
import MutedText from 'components/common/MutedText';
import DebouncedInput from 'components/common/DebouncedInput';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly, selectQuestionScheduling } from 'rtk/questionEditorSelector';
import { updateSchedulingConfig } from 'rtk/Question.Editor.Slice';
import { DEFAULT_SCHEDULING_CONFIG } from 'types/questions/scheduling.types';
import { useQuestionEditorContext } from '../context/create-question.context';
import SchedulingRepliesPopover from './SchedulingRepliesPopover';

const parseEventTypeId = (val: string | number): number => {
  const num = typeof val === 'number' ? val : parseInt(val, 10);
  return Number.isNaN(num) ? 0 : num;
};

/**
 * Recruiter settings panel for scheduling (cal.com booking) questions: the cal.com
 * event type ID and public booking link, plus the IANA timezone used when slots are
 * offered spoken on phone interviews. Chat embeds the booking UI; phone falls back
 * to emailing the booking link.
 */
const CreateScheduling = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const disabled = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const scheduling =
    useAppSelector((state) => selectQuestionScheduling(state, questionId)) ?? DEFAULT_SCHEDULING_CONFIG;

  return (
    <div className={clsx('flex flex-col justify-start gap-6', 'p-1')}>
      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel
            htmlFor="scheduling-event-type-id"
            className={clsx('whitespace-nowrap', disabled && 'opacity-50')}
          >
            {t('createScheduling.eventTypeIdLabel')}
          </HubertLabel>
          <DebouncedInput
            id="scheduling-event-type-id"
            type="number"
            disabled={disabled}
            value={scheduling.event_type_id || ''}
            min={1}
            placeholder={t('createScheduling.eventTypeIdPlaceholder')}
            onChange={(val) =>
              dispatch(updateSchedulingConfig({ questionId, patch: { event_type_id: parseEventTypeId(val) } }))
            }
          />
        </div>
        <MutedText text={t('createScheduling.eventTypeIdHint')} />
      </div>

      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel htmlFor="scheduling-cal-link" className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('createScheduling.calLinkLabel')}
          </HubertLabel>
          <DebouncedInput
            id="scheduling-cal-link"
            type="text"
            disabled={disabled}
            value={scheduling.cal_link}
            placeholder={t('createScheduling.calLinkPlaceholder')}
            onChange={(val) => dispatch(updateSchedulingConfig({ questionId, patch: { cal_link: String(val) } }))}
          />
        </div>
        <MutedText text={t('createScheduling.calLinkHint')} />
      </div>

      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel htmlFor="scheduling-timezone" className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('createScheduling.timezoneLabel')}
          </HubertLabel>
          <DebouncedInput
            id="scheduling-timezone"
            type="text"
            disabled={disabled}
            value={scheduling.timezone}
            placeholder={t('createScheduling.timezonePlaceholder')}
            onChange={(val) => dispatch(updateSchedulingConfig({ questionId, patch: { timezone: String(val) } }))}
          />
        </div>
        <MutedText text={t('createScheduling.timezoneHint')} />
      </div>

      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('createScheduling.repliesLabel')}
          </HubertLabel>
          <SchedulingRepliesPopover />
        </div>
        <MutedText text={t('createScheduling.repliesHint')} />
      </div>
    </div>
  );
};

export default CreateScheduling;
